import { validate } from 'uuid'

export const importJobStatuses = ['pending', 'processing', 'completed', 'processed_with_errors', 'failed'] as const
export type ImportJobStatus = (typeof importJobStatuses)[number]

export const knownRowErrorCodes = [
    'BAD_REQUEST',
    'COMPANY_REGISTRATION_COUNTRY_MISMATCH',
    'COMPANY_REGISTRATION_GERMANY',
    'CONTACTS_REQUIRED_FOR_INVOICE',
    'CONTACTS_REQUIRED_FOR_RECEIVABLE',
    'CREDIT_LIMIT_CONFLICT_ERROR',
    'CREDIT_LIMIT_REQUIRED_FOR_LOAN_CREATION',
    'CREDIT_LIMIT_STATE_ERROR',
    'CREDIT_LIMIT_STATUS_CONSTRAINT_VIOLATION',
    'CURRENCY_NOT_ALLOWED',
    'DEBTOR_NAME_REQUIRED_FOR_CREDIT_LIMIT',
    'FEATURE_NOT_AVAILABLE_FOR_COMPANY',
    'FORBIDDEN',
    'GUARANTOR_NOT_IN_CREDIT_LIMIT',
    'IDENTIFIER_CONTRAINT_VALIDATION_FAILED', // typo is in the backend — keep as-is
    'INCOHERENT_PAYMENT_DESTINATION',
    'INTERNAL_SERVER_ERROR',
    'INVALID_AMOUNT',
    'INVALID_ATTACHMENT_ID',
    'INVALID_ATTACHMENT_URL',
    'INVALID_DEBTOR_METADATA',
    'INVALID_LOAN_METADATA',
    'INVOICE_CONFLICT_ERROR',
    'INVOICE_RECEIVABLE_MISMATCH',
    'LOAN_AMOUNT_ERROR',
    'LOAN_CONFLICT_ERROR',
    'LOAN_CURRENCY_ERROR',
    'PAYMENT_TERM_TYPE_NOT_ENABLED',
    'QUOTE_CONFLICT_ERROR',
    'RECEIVABLE_CONFLICT',
    'RECEIVABLE_DEBTOR_MISMATCH',
    'RECEIVABLE_EXPECTED_INVOICE_DATE_NOT_ALLOWED',
    'REGISTRATION_NUMBER_VALIDATION_ERROR',
    'REPAYMENT_PERIOD_NOT_ALLOWED',
    'REPAYMENT_PERIOD_TOO_HIGH',
    'REPAYMENT_PERIOD_TOO_LOW',
    'RESOURCE_NOT_FOUND',
    'ROUTE_NOT_ENABLED'
] as const
export type KnownRowErrorCode = (typeof knownRowErrorCodes)[number]

export function isKnownRowErrorCode(code: string): code is KnownRowErrorCode {
    return (knownRowErrorCodes as readonly string[]).includes(code)
}

export const workflowTypes = [
    'create-loans-from-invoices',
    'create-loans-from-receivables',
    'link-invoices-to-receivables'
] as const
export type WorkflowType = (typeof workflowTypes)[number]

export function isWorkflowType(value: string): value is WorkflowType {
    return (workflowTypes as readonly string[]).includes(value)
}

export type ImportJob = {
    id: string
    companyId: string
    workflow: WorkflowType
    status: ImportJobStatus
    filePath?: string
    assetsCount: number
    outputFilePath?: string
    totalRows: number | null
    processedRows: number | null
    successRows: number | null
    errorRows: number | null
    errorCode?: string
    errorMessage?: string
    createdAt: string
    updatedAt: string
}

export type CreateImportJobPayload = {
    companyId: string
    workflow: WorkflowType
    filePath: string
}

export type WorkflowConfig = {
    id: WorkflowType
    companyId: string
    workflowType: WorkflowType
    enabled: boolean
    description?: string
    templateUrl?: string
}

export type UploadUrlResponse = {
    fileName: string
    uploadUrl: string
    filePath: string
    contentType: string
}

export type UploadUrlsResponse = {
    uploadId: string
    csvFilePath: string
    expiresAt: string
    uploadUrls: UploadUrlResponse[]
}

export type GetUploadUrlsParams = {
    companyId: string
    workflow: string
    fileNames: string[]
}

export type WorkflowColumns = {
    required: string[]
}

export const workflowColumns: Record<WorkflowType, WorkflowColumns> = {
    'create-loans-from-invoices': {
        required: [
            'debtor_identifier_type',
            'debtor_identifier_value',
            'debtor_name',
            'debtor_address_country',
            'debtor_contact_email',
            'debtor_contact_phone_number',
            'invoice_total_amount_including_taxes',
            'invoice_total_amount_excluding_taxes',
            'invoice_currency',
            'invoice_date',
            'invoice_number',
            'invoice_payment_term_type',
            'invoice_payment_term',
            'user_id',
            'loan_amount',
            'loan_payment_destination_account_number_type',
            'loan_payment_destination_account_number',
            'loan_payment_destination_routing_number_type',
            'loan_payment_destination_routing_number'
        ]
    },
    'create-loans-from-receivables': {
        required: [
            'debtor_identifier_type',
            'debtor_identifier_value',
            'debtor_name',
            'debtor_address_country',
            'debtor_contact_email',
            'debtor_contact_phone_number',
            'receivable_total_amount_including_taxes',
            'receivable_total_amount_excluding_taxes',
            'receivable_currency',
            'receivable_delivered_at',
            'receivable_reference',
            'receivable_expected_invoice_date',
            'receivable_expected_invoice_payment_term_type',
            'receivable_expected_invoice_payment_term',
            'user_id',
            'loan_amount',
            'loan_payment_destination_account_number_type',
            'loan_payment_destination_account_number',
            'loan_payment_destination_routing_number_type',
            'loan_payment_destination_routing_number'
        ]
    },
    'link-invoices-to-receivables': {
        required: ['invoice_number', 'receivable_references']
    }
}

export type DownloadUrlResponse = {
    downloadUrl: string
    expiresAt: string
}

export const getWorkflowLabel = (
    t: ReturnType<typeof import('next-intl').useTranslations<'imports'>>,
    workflow: string
): string => (t.has(`workflowTypes.${workflow}`) ? t(`workflowTypes.${workflow}`) : workflow)

const MISSING_COLUMNS_PATTERN = /Missing required columns?:\s*(.+)/i

export function parseErrorDetail(
    errorCode: string | undefined,
    errorMessage: string | undefined
): { key: string; values: Record<string, string> } | null {
    if (!errorCode || !errorMessage) return null

    if (errorCode === 'VALIDATION_ERROR') {
        const match = errorMessage.match(MISSING_COLUMNS_PATTERN)
        if (match) return { key: 'errors.missingColumns', values: { columns: match[1].trim() } }
    }

    return null
}

export const extractFilename = (filePath: string | undefined): string => {
    if (!filePath) return '-'
    const filename = filePath.split('/').pop() || filePath
    const [prefix, ...rest] = filename.split('_')
    if (validate(prefix)) return rest.join('_')
    return filename
}
